/* ============================================================
   LAYER'D QUANTITY ENGINE  ·  window.LAYERD.quantities
   ------------------------------------------------------------
   ONE source of truth for every cake quantity the studio works
   out — batter areas, ganache ratios, filling densities,
   buttercream coats/filling, batch conversion and bowl checks.

   Consumed by BOTH Batch Math (the calculators) and the Planner
   (task quantities + shopping roll-up). Two engines would drift;
   this file is the one, so a booking's grams are the same figure
   on the task, the batch sheet and the shopping list. (Addendum
   Brief §4.)

   Every function is PURE: it takes a `cfg` object (the same shape
   as the #bmath-config island / the shared learn-quantities-config
   snippet) plus plain inputs, and returns numbers. No DOM, no
   localStorage, no units labelling — those stay in the UIs. Pass
   the page's cfg in; when a key is missing the DEFAULTS below
   stand in (they mirror the shipped config so a page that hasn't
   wired the island yet still agrees to the gram).

   The §2.1 rule lives HERE, not in either UI: buttercream always
   counts crumb + final COATS; filling is a separate product that
   is only in the total when `fillOn` is true — so the two can
   never be blended differently in two places.
   ============================================================ */
(function () {
  'use strict';

  /* ---- canonical defaults (mirror the shipped #bmath-config) ----
     The config island overrides these per page. They are the same
     fallbacks Batch Math already carried inline, centralised. */
  var DEFAULTS = {
    /* 2026-08-11: her own recipe — 200 g liquid whites / 335 g superfine
       sugar / 600 g butter + 4 g vanilla extract + 2 g kosher salt = 1,141 g. The old 875 was
       back-calculated from bowl fills and the recipe was bent to fit it;
       the recipe is the fact and the batch follows. See the _batchGrams
       note in snippets/learn-quantities-config.liquid. */
    batchGrams: 1140,
    /* r183 (2026-08-29, Mariya): "maybe someone is using a different
       buttercream - we shouldn't specify it's called Swiss meringue." The
       gram maths are still calibrated to the studio's SMBC, and the recipe
       card in Recipes still names itself properly; what changes is the UNIT
       LABEL, which was telling every baker her buttercream had to be ours. */
    batchLabel: 'Buttercream',
    gramsPerCup: 215,
    insurancePct: 10,
    /* she weighs liquid whites, so grams is the honest shopping line; the
       count survives only as a fallback for anyone cracking shells */
    eggWhitesPerBatch: 6,
    eggWhiteGramsPerBatch: 200,
    heartAreaFactor: 0.875,
    coat: { crumb: 0.98, sharp: 2.51, rustic: 3.82, piped: 6.54 }, /* 2026-09-04 x1.09, calibrated to Y&D — see snippet _coat */
    filling: { thin: 2.3, standard: 3.5, generous: 5.2 },
    /* ---- DAMS (2026-08-04, calibrated from Mariya's own bake) ----
       A dam is piped BUTTERCREAM, so its weight belongs to the buttercream
       budget — and it was missing entirely. Her bench data: a 10″/6-layer +
       8″/5-layer cake, two dams per layer, used ~2,450 g of SMBC. The coat
       rates account for ~1,417 g of that (pre-insurance), leaving ~1,033 g
       of dam across 386 in of piped rope = 2.67 g/in. Held at 2.65.
       Back-solved that is a rope of roughly 1.2 × 0.9 cm — a real dam.
       `modes`: 1 = the outer edge only; 2 = her studio method, a second
       ring halfway to the centre that sets firm and carries load. */
    /* 2026-09-04 (Mariya's call): 2.65 -> 4.0 from a 6" bench — one outer
       dam 73-80 g / 18.85 in ring = ~3.9-4.2 g/in. CAVEAT: 4.0 flat now
       OVER-quotes the 8-10" cakes 2.65 was validated on (~2,450 g total);
       a size-aware rate is the real fix once more small-cake data lands. */
    /* 2026-09-08 (Mariya's small-cake bench, PER GAP, outer + inner dam):
       7" = 40 g outer + 15 g inner = 55 g / (π·7 × 1.5 = 32.99 in) = 1.67 g/in;
       5" = 33 g / (π·5 × 1.5 = 23.56 in) = 1.40 g/in. The old 4.0 quoted a 7"
       gap dam at ~132 g — 2-3× reality. Flat 1.6 g/in fits both (7" 52.8 g /
       −4%, 5" 37.7 g / +14%, erring safe on the smaller cake). This supersedes
       the 6"-bench 4.0; a size-aware curve still wants more points to be real. */
    dam: { gramsPerInch: 1.6, innerRingFactor: 0.5 },
    fillings: {
      estimate: false,
      /* one combined number (Mariya 2026-07-16): dam + crémeux + compote per
         filling layer, calibrated from six studio weigh-ins. */
      types: {
        studio: { label: 'Filling, dam + soft filling (the studio mix)', gPerSqIn: 6.5 }
      }
    },
    ganache: {
      ratios: {
        dark: { drip: 1.0, coat: 2.0, filling: 2.0 },
        milk: { drip: 2.0, coat: 2.5, filling: 2.5 },
        white: { drip: 2.5, coat: 3.0, filling: 3.0 }
      },
      dripGramsPerInch: 6,
      coatGramsPerSqIn: 3.0,
      /* 2026-09-04: 3.0 -> 4.75 from Mariya's salted caramel ganache 6" bench
         (130-140 g/layer / 28.3 in2 = ~4.6-5.0 g/in2). Global to all ganache
         fillings; plain dark may run lighter than caramel — revisit per type. */
      fillingGramsPerSqIn: 4.75
    },
    batter: {
      quarterSheet: [9, 13], halfSheet: [13, 18],
      quarterYield: { 4: 5, 5: 4, 6: 3, 7: 2, 8: 1.5, 9: 1 },
      halfYield: { 4: 10, 5: 8, 6: 6, 7: 4, 8: 3, 9: 2, 10: 1.5, 11: 1, 12: 1 },
      roundRefIn: 8, roundRefLayers: 2
    },
    bowl: {
      default: '5',
      sizes: {
        '5': { label: '5 qt stand-mixer bowl', batterQts: 2, smbcBatchesPerBowl: 2 },
        /* r62 REVERTED r86 (2026-08-23): the box says 6 qt after all — the
           numbers never moved either time; '7' is the legacy alias now for
           devices that stored it during the r62 window. */
        '6': { label: '6 qt stand-mixer bowl', batterQts: 3, smbcBatchesPerBowl: 3 },
        '7': { label: '6 qt stand-mixer bowl', batterQts: 3, smbcBatchesPerBowl: 3, legacy: true }
      }
    }
  };

  /* small readers that fall back to DEFAULTS key by key */
  function num(cfg, key, dflt) { var v = cfg && cfg[key]; return (v == null || v === '') ? dflt : +v; }
  function obj(cfg, key, dflt) { return (cfg && cfg[key]) || dflt; }

  /* ============================================================
     USER CALIBRATION (r95, 2026-08-24 — Mariya's design call).
     The studio rates are HER bench data; another baker's recipes
     run heavier or lighter, and the tool should learn that
     instead of arguing. One per-device store holds a multiplier
     per product (1 = the studio model); every product function
     applies its factor at the RATE level, so per-tier lines,
     totals, batches, splits, the Planner's task quantities and
     the shopping roll-up all move together — the one-engine
     "same figure everywhere" contract is preserved BY DESIGN,
     the whole suite just agrees with the user now.

     This is the engine's ONE sanctioned impurity: a guarded
     localStorage READ per call (the header's "no localStorage"
     rule otherwise stands — the engine still never writes; the
     UIs write through Q.calibration.set below). Factors clamp to
     0.5–2 so a typo can never silently halve a wedding cake. */
  var CAL_KEY = 'layerd-quantity-calibration';
  var CAL_PRODUCTS = ['batter', 'fillings', 'buttercream', 'ganache'];
  function calRead() {
    try {
      if (typeof localStorage === 'undefined') return null;
      var v = JSON.parse(localStorage.getItem(CAL_KEY));
      if (v && v.v === 1) return v;
    } catch (e) { /* private mode / corrupt — studio model */ }
    return null;
  }
  function calFactor(product) {
    var c = calRead();
    var f = c && +c[product];
    if (!f || !isFinite(f)) return 1;
    return Math.min(2, Math.max(0.5, f));
  }
  /* r223 (Phase 3): a per-recipe factor, stored beside the per-product ones
     under `recipes`. Returns null when a recipe has not been tuned, so the
     caller falls back to the product factor. Same 0.5-2 clamp. A recipe factor
     STOPS being a global fudge - only that recipe's figures move. */
  function calRecipeFactor(id) {
    if (!id) return null;
    var c = calRead();
    var f = c && c.recipes && +c.recipes[id];
    if (!f || !isFinite(f)) return null;
    return Math.min(2, Math.max(0.5, f));
  }
  var calibration = {
    key: CAL_KEY,
    products: CAL_PRODUCTS,
    factor: calFactor,
    get: function () {
      var out = {};
      CAL_PRODUCTS.forEach(function (p) { out[p] = calFactor(p); });
      return out;
    },
    set: function (product, f) {
      if (CAL_PRODUCTS.indexOf(product) === -1) return false;
      f = Math.min(2, Math.max(0.5, +f || 1));
      var c = calRead() || { v: 1 };
      c[product] = Math.round(f * 100) / 100;
      try { localStorage.setItem(CAL_KEY, JSON.stringify(c)); } catch (e) { return false; }
      /* the same synthetic event the settings panel uses, so every
         open surface refreshes without its own wiring */
      try { window.dispatchEvent(new StorageEvent('storage', { key: CAL_KEY })); } catch (e2) { }
      return true;
    },
    reset: function (product) { return calibration.set(product, 1); },
    /* r223 (Phase 3): per-recipe tuning. recipeFactor(id) resolves the saved
       factor (or null); setRecipe/resetRecipe write it under `recipes`. The
       engine prefers a recipe factor over the product one when a recipeId is
       in scope, so tuning one filling never moves the others. */
    recipeFactor: calRecipeFactor,
    setRecipe: function (id, f) {
      if (!id) return false;
      f = Math.min(2, Math.max(0.5, +f || 1));
      var c = calRead() || { v: 1 };
      if (!c.recipes) c.recipes = {};
      c.recipes[id] = Math.round(f * 100) / 100;
      try { localStorage.setItem(CAL_KEY, JSON.stringify(c)); } catch (e) { return false; }
      try { window.dispatchEvent(new StorageEvent('storage', { key: CAL_KEY })); } catch (e2) { }
      return true;
    },
    resetRecipe: function (id) {
      var c = calRead();
      if (!c || !c.recipes || c.recipes[id] == null) return false;
      delete c.recipes[id];
      try { localStorage.setItem(CAL_KEY, JSON.stringify(c)); } catch (e) { return false; }
      try { window.dispatchEvent(new StorageEvent('storage', { key: CAL_KEY })); } catch (e2) { }
      return true;
    }
  };

  /* ---------- geometry (inches) ---------- */
  function heartFactor(cfg) { return num(cfg, 'heartAreaFactor', DEFAULTS.heartAreaFactor); }
  /* §2.2: a heart pan holds less than the round of the same size — its
     TOP AREA is heartAreaFactor × the round (drives batter, filling,
     buttercream-top). Perimeter-based numbers (sideArea, edgeLength) use
     the bounding round, i.e. the round/else branch, matching Structure. */
  function topArea(cfg, t) {
    if (t.shape === 'square') return t.d * t.d;
    var round = Math.PI * t.d * t.d / 4;
    return t.shape === 'heart' ? round * heartFactor(cfg) : round;
  }
  function sideArea(t) { return (t.shape === 'square' ? 4 * t.d : Math.PI * t.d) * t.h; }
  function edgeLength(t) { return t.shape === 'square' ? 4 * t.d : Math.PI * t.d; }

  /* ---------- batch conversion (the 5% sliver rule) ----------
     Whole batches, but a spill of ≤5% over a whole batch rounds DOWN —
     the insurance margin absorbs the sliver rather than sending anyone
     off to make a whole extra batch. Shared by buttercream and batter. */
  /* `sliverPct` is how far OVER a whole batch still rounds down. It exists
     because sending someone back to the mixer for a 2% spill is absurd WHEN
     THE ESTIMATE IS TRUSTWORTHY — the insurance margin absorbs it.

     2026-08-11: buttercream now passes 0 (see below). Batter keeps 0.05. */
  function batchConvert(total, batchGrams, opts) {
    var batches = total / batchGrams;
    var sliver = (opts && opts.sliverPct != null) ? +opts.sliverPct : 0.05;
    var whole = Math.floor(batches);
    var frac = batches - whole;
    var make = (whole >= 1 && frac <= sliver) ? whole : whole + (frac > 0 ? 1 : 0);
    if (make < 1) make = 1;
    return { batches: batches, makeBatches: make, leftover: make * batchGrams - total };
  }

  /* ---------- the mixer bowl ----------
     bowlKey is the baker's choice ('5' | '6'; '7' = legacy alias of '6', r86); the UI owns where that
     comes from (a per-device localStorage pref). Returns resolved
     numbers only. */
  function bowlCfg(cfg, bowlKey) {
    var b = obj(cfg, 'bowl', DEFAULTS.bowl);
    if (b.sizes) {
      var dflt = b.default || '5';
      var key = (bowlKey && b.sizes[bowlKey]) ? bowlKey : dflt;
      b = b.sizes[key] || b.sizes[dflt] || {};
    }
    return {
      label: b.label || '5 qt stand-mixer bowl',
      batterQts: b.batterQts || 3,
      smbcPerBowl: b.smbcBatchesPerBowl || 1
    };
  }
  function batterQtsOf(areaLayers) { return areaLayers / 57.75; } /* 1 US qt = 57.75 in³ */
  /* numbers only; the UI builds the "qt / L, N rounds" sentence */
  function bowlFit(cfg, bowlKey, areaLayers) {
    var b = bowlCfg(cfg, bowlKey);
    var q = batterQtsOf(areaLayers);
    var mixes = Math.max(1, Math.ceil(q / b.batterQts - 0.05));
    return { fits: mixes <= 1, mixes: mixes, qts: q, bowl: b };
  }

  /* ============================================================
     BUTTERCREAM — coats (always) + filling (only when fillOn)
     s = { tiers, finish, fillOn, layers, fillThick }
     opts = { bowl } (optional bowl key for mixer runs)
     ============================================================ */
  function buttercream(cfg, s, opts) {
    var coat = obj(cfg, 'coat', DEFAULTS.coat);
    var filling = obj(cfg, 'filling', DEFAULTS.filling);
    var batchGrams = num(cfg, 'batchGrams', DEFAULTS.batchGrams);
    var insurancePct = num(cfg, 'insurancePct', DEFAULTS.insurancePct);
    var gramsPerCup = num(cfg, 'gramsPerCup', DEFAULTS.gramsPerCup);
    var eggPerBatch = num(cfg, 'eggWhitesPerBatch', DEFAULTS.eggWhitesPerBatch);

    /* dams: 0 = none, 1 = outer edge, 2 = outer + an inner ring halfway in.
       Per-tier `layers` wins over the global one, because a cake can carry a
       6-layer base under a 5-layer top (Mariya's own) and the dam count
       follows the gaps in EACH tier. Absent = 0, so every existing caller
       that knows nothing about dams gets exactly its old number. */
    var damCfg = obj(cfg, 'dam', DEFAULTS.dam);
    var damMode = Math.max(0, Math.min(2, Math.round(+s.dams || 0)));
    var calF = calFactor('buttercream');   /* r95: the user's tuning, at rate level */
    var per = s.tiers.map(function (tier) {
      var surface = sideArea(tier) + topArea(cfg, tier);
      var crumb = surface * coat.crumb * calF;
      var coatG = surface * coat[s.finish] * calF;
      /* the §2.1 rule: filling is its own product, only present when on */
      var fill = s.fillOn ? (s.layers - 1) * topArea(cfg, tier) * filling[s.fillThick] * calF : 0;
      var gaps = Math.max(0, (+tier.layers || +s.layers || 1) - 1);
      var ring = damMode >= 1 ? edgeLength(tier) : 0;
      if (damMode >= 2) ring += edgeLength(tier) * damCfg.innerRingFactor;
      var dam = gaps * ring * damCfg.gramsPerInch * calF;
      return { tier: tier, crumb: crumb, coat: coatG, fill: fill, dam: dam,
        total: crumb + coatG + fill + dam };
    });
    var r = { per: per, crumb: 0, coat: 0, fill: 0, dam: 0 };
    per.forEach(function (p) { r.crumb += p.crumb; r.coat += p.coat; r.fill += p.fill; r.dam += p.dam; });
    r.coatsSubtotal = r.crumb + r.coat;         /* the two products, kept apart */
    r.fillingSubtotal = r.fill;
    r.damSubtotal = r.dam;
    r.damMode = damMode;
    r.subtotal = r.crumb + r.coat + r.fill + r.dam;
    r.insurance = r.subtotal * insurancePct / 100;
    r.total = r.subtotal + r.insurance;
    r.cups = r.total / gramsPerCup;
    /* ---- BUTTERCREAM NEVER ROUNDS DOWN (2026-08-11) ----
       Mariya's Yasmin & Donte bake: 10″×7″ 6-layer + 7″×6″ 5-layer, sharp,
       two dams. The model predicted 2,474 g of usage; she used 3,106 g —
       25% light. The advice ("4 batches") was still right, but only because
       the rounding happened to go up; a sweep of every two-tier round/sharp/
       two-dam cake found 53% where it would have gone DOWN and left her
       short mid-decorate.

       The sliver rule assumes the estimate underneath is accurate. While it
       is knowingly light, rounding down converts a modelling error into a
       trip back to the mixer with a half-coated cake on the bench — the
       expensive failure. Rounding up costs at most one spare batch.

       Deliberately NOT half-batch granularity, which would cap the waste:
       on her actual cake that lands on 3.5 batches = 3,062 g against 3,106 g
       used — 44 g SHORT. Worth revisiting once the rates are recalibrated
       from logged bakes, not before.
       `bcSliverPct` is config so this is tunable without a code change. */
    var conv = batchConvert(r.total, batchGrams, { sliverPct: num(cfg, 'bcSliverPct', 0) });
    r.batches = conv.batches;
    r.makeBatches = conv.makeBatches;
    r.leftover = conv.leftover;
    r.eggWhites = eggPerBatch ? r.makeBatches * eggPerBatch : 0;
    /* the number the shopping line actually shows — she buys carton whites
       by weight, so a count of shells is the wrong unit for her */
    var eggGramsPerBatch = num(cfg, 'eggWhiteGramsPerBatch', DEFAULTS.eggWhiteGramsPerBatch);
    r.eggWhiteGrams = eggGramsPerBatch ? r.makeBatches * eggGramsPerBatch : 0;
    r.mixerRuns = Math.max(1, Math.ceil(r.makeBatches / bowlCfg(cfg, opts && opts.bowl).smbcPerBowl));
    return r;
  }

  /* ============================================================
     GANACHE — drip / full coat / filling
     s = { tiers, app, choc, layers }
     ============================================================ */
  function ganache(cfg, s) {
    var g = obj(cfg, 'ganache', DEFAULTS.ganache);
    var calF = calFactor('ganache');   /* r95 */
    var per = s.tiers.map(function (tier) {
      var grams;
      if (s.app === 'drip') grams = edgeLength(tier) * g.dripGramsPerInch;
      else if (s.app === 'coat') grams = (sideArea(tier) + topArea(cfg, tier)) * g.coatGramsPerSqIn;
      else grams = (s.layers - 1) * topArea(cfg, tier) * g.fillingGramsPerSqIn;
      return { tier: tier, grams: grams * calF };
    });
    var subtotal = 0;
    per.forEach(function (p) { subtotal += p.grams; });
    /* 2026-08-11 (her call): the same insurance margin buttercream has
       always carried. Ganache was raw area x rate with NO cushion at all —
       the exact figure and not a gram more — so any scrap left on the bowl
       or the spatula came straight out of the cake. She asked for "a little
       extra rather than not enough" and this is her existing 10%, not a new
       number. The chocolate/cream SPLIT is taken after the margin so the
       two weigh-outs still add up to what you are told to make. */
    var insurancePct = num(cfg, 'insurancePct', DEFAULTS.insurancePct);
    var insurance = subtotal * insurancePct / 100;
    var total = subtotal + insurance;
    var ratio = g.ratios[s.choc][s.app];
    return {
      per: per, subtotal: subtotal, insurance: insurance, insurancePct: insurancePct,
      total: total, ratio: ratio,
      choc: total * ratio / (ratio + 1),
      cream: total / (ratio + 1)
    };
  }

  /* ============================================================
     FILLINGS — compotes, crémeux, montées
     s = { tiers, type }
     ============================================================ */
  function fillingTypes(cfg) { return (cfg && cfg.fillings && cfg.fillings.types) || DEFAULTS.fillings.types; }
  /* filling beds derive from tier height: sponge layers = h−1, beds
     between them = sponge−1 (her 4" standard = 3 sponge, 2 beds) */
  function fillingLayersOf(t) { return Math.max(1, ((t.h || 4) - 1) - 1); }
  /* r223 (2026-09-16): filling weight = volume × density. A type that carries
     an explicit gPerSqIn keeps it verbatim (the studio's own weighed rate, so
     no golden drift); a type with only a density resolves the rate from the
     shared bed depth: g/in² = depthIn × (in³→ml) × density. 1 in³ = 16.387 ml. */
  var IN3_TO_ML = 16.387;
  function fillRateGPerSqIn(cfg, ty) {
    if (!ty) return 0;
    if (ty.gPerSqIn != null) return +ty.gPerSqIn;
    var fc = (cfg && cfg.fillings) || DEFAULTS.fillings;
    var bedMm = (fc && fc.bedDepthMm != null) ? +fc.bedDepthMm : 9;
    return (bedMm / 25.4) * IN3_TO_ML * (+ty.density || 0);
  }
  function fillings(cfg, s) {
    var types = fillingTypes(cfg);
    /* a type key that no longer exists (e.g. a saved 'jellies' from before
       the 2026-07-16 combined-number calibration) falls back to the FIRST
       configured type, so legacy states land on the current studio rate */
    var ty = types[s.type];
    if (!ty) { for (var k in types) { ty = types[k]; break; } }
    ty = ty || { label: s.type, gPerSqIn: 3 };
    var estimate = !!(cfg && cfg.fillings ? cfg.fillings.estimate : DEFAULTS.fillings.estimate);
    var out = { type: s.type, label: ty.label, per: [], subtotal: 0, total: 0, estimate: estimate };
    /* r95 + r223: a per-recipe factor (when this filling is a tuned recipe)
       wins over the per-product one, so tuning one filling moves only it. */
    var rf = (s.recipeId != null) ? calRecipeFactor(s.recipeId) : null;
    var calF = (rf != null) ? rf : calFactor('fillings');
    out.tunedRecipe = (rf != null);
    var rate = fillRateGPerSqIn(cfg, ty);   /* r223: density × bed, or the studio's weighed rate */
    (s.tiers || []).forEach(function (t) {
      var layers = fillingLayersOf(t);
      var gg = topArea(cfg, t) * layers * rate * calF;
      out.per.push({ tier: t, layers: layers, g: gg });
      out.subtotal += gg;
    });
    /* 2026-08-11 (her call): fillings carried NO margin — the rate is a
       median of six weigh-ins that scattered +/-25%, and the tool was
       quoting the median as if it were exact. The same 10% buttercream
       uses. `subtotal` keeps the raw area maths so the per-tier lines
       still add up to it; `total` is what to actually make. */
    out.insurancePct = num(cfg, 'insurancePct', DEFAULTS.insurancePct);
    out.insurance = out.subtotal * out.insurancePct / 100;
    out.total = out.subtotal + out.insurance;
    return out;
  }

  /* ============================================================
     BATTER — how much sponge, in sheet batches or round batches
     s = { tiers:[{d,shape,layers,flavour}], vessel:'sheet'|'round',
           roundRef, roundRefLayers }
     ============================================================ */
  function batterPans(cfg) {
    var b = obj(cfg, 'batter', DEFAULTS.batter);
    return { quarter: b.quarterSheet || DEFAULTS.batter.quarterSheet, half: b.halfSheet || DEFAULTS.batter.halfSheet };
  }
  function batterYields(cfg) {
    var b = obj(cfg, 'batter', DEFAULTS.batter);
    return { quarter: b.quarterYield || DEFAULTS.batter.quarterYield, half: b.halfYield || DEFAULTS.batter.halfYield };
  }
  function ctRoundUp(exact) {
    var whole = Math.floor(exact);
    var make = (whole >= 1 && (exact - whole) <= 0.05) ? whole : whole + ((exact - whole) > 0 ? 1 : 0);
    return make < 1 ? 1 : make;
  }
  /* tiers sharing a flavour (case-insensitive; blank = one unnamed group)
     share pans and batches; different flavours never do */
  function batterGroups(per) {
    var order = [], byKey = {};
    per.forEach(function (p) {
      var label = ((p.tier.flavour || '') + '').trim();
      var k = label.toLowerCase();
      if (!byKey[k]) { byKey[k] = { label: label, per: [] }; order.push(byKey[k]); }
      byKey[k].per.push(p);
    });
    return order;
  }
  function batter(cfg, s) {
    var per = s.tiers.map(function (t) { return { tier: t, layers: t.layers || 1, flavour: t.flavour }; });
    var out = { per: per, vessel: s.vessel, roundRef: s.roundRef };
    out.areaLayers = 0;
    per.forEach(function (p) { out.areaLayers += topArea(cfg, p.tier) * p.layers; });
    var groups = batterGroups(per);
    out.groups = groups;
    /* r95: batter tunes at the COVERAGE level — a factor above 1 means
       "my batch covers less than the model assumes", so the reference
       area (round) and the layers-per-pan yields (sheet) shrink by it.
       Pan counts stay whole; batches follow honestly. */
    var calF = calFactor('batter');
    if (s.vessel === 'round') {
      var layersPerBatch = s.roundRefLayers || 1;
      var refArea = (Math.PI * s.roundRef * s.roundRef / 4) * layersPerBatch / calF;
      out.exact = 0; out.batches = 0;
      groups.forEach(function (g) {
        var area = 0;
        g.per.forEach(function (p) { area += topArea(cfg, p.tier) * p.layers; });
        g.exact = refArea > 0 ? area / refArea : 0;
        g.batches = ctRoundUp(g.exact);
        out.exact += g.exact;
        out.batches += g.batches;
      });
    } else {
      var Y = batterYields(cfg);
      var hp = batterPans(cfg).half, halfArea = hp[0] * hp[1] / calF;
      out.qSheets = 0; out.hSheets = 0; out.batchesQ = 0; out.pieced = [];
      groups.forEach(function (g) {
        var byRing = {};
        g.per.forEach(function (p) {
          var k = p.tier.d;
          if (!byRing[k]) byRing[k] = { layers: 0, area: 0 };
          byRing[k].layers += p.layers;
          byRing[k].area += topArea(cfg, p.tier) * p.layers;
        });
        g.qSheets = 0; g.hSheets = 0; g.hSizes = []; g.pieced = [];
        Object.keys(byRing).forEach(function (d) {
          if (Y.quarter[d] != null) { g.qSheets += Math.ceil(byRing[d].layers * calF / Y.quarter[d]); }
          else if (Y.half[d] != null) { g.hSheets += Math.ceil(byRing[d].layers * calF / Y.half[d]); g.hSizes.push(+d); }
          else {
            g.hSheets += Math.ceil(byRing[d].area / halfArea);
            g.hSizes.push(+d);
            g.pieced.push(+d);
          }
        });
        g.hSizes.sort(function (a, b) { return a - b; });
        g.batchesQ = g.qSheets + 2 * g.hSheets;
        out.qSheets += g.qSheets; out.hSheets += g.hSheets; out.batchesQ += g.batchesQ;
        g.pieced.forEach(function (d) { if (out.pieced.indexOf(d) === -1) out.pieced.push(d); });
      });
      out.pieced.sort(function (a, b) { return a - b; });
    }
    return out;
  }


  /* ---------- r127: the batch sheet's display snapshot ----------
     booking.batchSheet[tool].outputs, built from that tool's stored
     inputs. It lived in learn-batch-math.js, but the Planner needs it too
     when a resize makes a sheet re-derive itself and only the engine is
     loaded there. One implementation; Batch Math delegates to this.
     `opts.bowl` is the baker's mixer for buttercream mixer-runs; omitted,
     the engine default applies. */
  function sheetOutputs(tool, cfg, inputs, opts) {
    if (!inputs) return {};
    if (tool === 'bc') {
      var r = buttercream(cfg, inputs, opts || {});
      return { total: Math.round(r.total), coats: Math.round(r.coatsSubtotal),
        fill: Math.round(r.fillingSubtotal), fillOn: !!inputs.fillOn,
        makeBatches: r.makeBatches, eggWhites: r.eggWhites, mixerRuns: r.mixerRuns };
    }
    if (tool === 'ga') {
      var g = ganache(cfg, inputs);
      return { total: Math.round(g.total), choc: Math.round(g.choc),
        cream: Math.round(g.cream), ratio: g.ratio, app: inputs.app, chocType: inputs.choc };
    }
    if (tool === 'ct') {
      var c = batter(cfg, inputs);
      return c.vessel === 'round'
        ? { vessel: 'round', batches: c.batches }
        : { vessel: 'sheet', batchesQ: c.batchesQ, qSheets: c.qSheets, hSheets: c.hSheets };
    }
    if (tool === 'fl') {
      var f = fillings(cfg, inputs);
      return { total: Math.round(f.total / 5) * 5, type: inputs.type,
        label: f.label, estimate: f.estimate };
    }
    return {};
  }

  /* Re-derive every calculator the resize cascade marked. Pure recompute
     of stored inputs - it never invents an input she did not save. */
  function healSheet(bk, cfg, opts) {
    var sheet = bk && bk.batchSheet;
    if (!sheet) return false;
    var healed = false;
    ['ct', 'ga', 'fl', 'bc'].forEach(function (tool) {
      var entry = sheet[tool];
      if (!entry || !entry._recompute || !entry.inputs || entry.skipped) return;
      try {
        entry.outputs = sheetOutputs(tool, cfg, entry.inputs, opts);
        entry.savedAt = Date.now();
        delete entry._recompute;
        healed = true;
      } catch (e) { /* leave the flag; a later load can try again */ }
    });
    return healed;
  }

  /* ---------- publish ---------- */
  window.LAYERD = window.LAYERD || {};
  window.LAYERD.quantities = {
    version: 1,
    defaults: DEFAULTS,
    calibration: calibration,   /* r95: the user's per-product tuning */
    /* geometry + helpers */
    topArea: topArea, sideArea: sideArea, edgeLength: edgeLength,
    heartFactor: heartFactor,
    batchConvert: batchConvert,
    bowlCfg: bowlCfg, batterQtsOf: batterQtsOf, bowlFit: bowlFit,
    /* the quantity computes */
    buttercream: buttercream,
    ganache: ganache,
    fillings: fillings, fillingTypes: fillingTypes, fillingLayersOf: fillingLayersOf,
    batter: batter, batterPans: batterPans, batterYields: batterYields,
    /* r127: the batch sheet's snapshot + self-heal, shared so the
       Planner can re-derive a resized sheet without the BM bundle */
    sheetOutputs: sheetOutputs, healSheet: healSheet
  };
})();
